import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, Gift, FileText, Tag, Newspaper, ShoppingBag, Quote, Mail, MessageCircle, Globe, Instagram, Facebook, ArrowRight, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { resolveImageUrl } from "../../context/AppSettingsContext";
import LoyaltyCard from "../LoyaltyCard";
import { GiftCardVisual } from "../GiftCardVisual";
import { InitiativeTile } from "../InitiativeSheet";

const img = (u) => (u ? resolveImageUrl(u) : null);
export const Section = ({ id, s, brand, children, dark }) => (
  <section id={id} className={`scroll-mt-16 ${dark ? "text-white" : ""}`} style={dark ? { background: brand } : undefined} data-testid={`cl-section-${id}`}>
    <div className="max-w-5xl mx-auto px-5 py-12 sm:py-16">
      <h2 className={`font-heading text-2xl sm:text-3xl font-extrabold tracking-tight ${dark ? "" : "text-zinc-900"}`} style={dark ? { color: "#fff" } : undefined}>{s.title}</h2>
      {s.subtitle && <p className={`mt-2 text-base md:text-lg ${dark ? "text-white/85" : "text-zinc-600"}`}>{s.subtitle}</p>}
      <div className="mt-8">{children}</div>
    </div>
  </section>
);

export function Gallery({ photos, brand }) {
  const [i, setI] = useState(0);
  if (!photos?.length) return null;
  return (
    <div className="relative rounded-2xl overflow-hidden aspect-[16/10] flex items-center justify-center" style={{ background: `${brand}14` }} data-testid="cl-gallery">
      <MapPin className="h-10 w-10" style={{ color: brand }} />
      <img src={img(photos[i])} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      {photos.length > 1 && (<>
        <button type="button" onClick={() => setI((i - 1 + photos.length) % photos.length)} className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 flex items-center justify-center shadow" aria-label="Precedente" data-testid="cl-gallery-prev"><ChevronLeft className="h-5 w-5" /></button>
        <button type="button" onClick={() => setI((i + 1) % photos.length)} className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 flex items-center justify-center shadow" aria-label="Successiva" data-testid="cl-gallery-next"><ChevronRight className="h-5 w-5" /></button>
        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">{photos.map((_, k) => <span key={k} className="h-1.5 w-1.5 rounded-full" style={{ background: k === i ? brand : "rgba(255,255,255,.7)" }} />)}</div>
      </>)}
    </div>
  );
}

export function Stores({ branches, brand }) {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {branches.map((b) => {
        const photos = [...(b.gallery || []), ...(b.store_image_url && !(b.gallery || []).includes(b.store_image_url) ? [b.store_image_url] : [])];
        const q = encodeURIComponent(b.address || b.name);
        return (
          <article key={b.id} className="rounded-3xl bg-white ring-1 ring-zinc-200 overflow-hidden shadow-sm" data-testid={`cl-store-${b.id}`}>
            {photos.length ? <Gallery photos={photos} brand={brand} /> : <div className="aspect-[16/10] flex items-center justify-center" style={{ background: `${brand}14` }}><MapPin className="h-10 w-10" style={{ color: brand }} /></div>}
            <div className="p-5 space-y-2">
              <h3 className="font-heading font-bold text-lg text-zinc-900">{b.name}</h3>
              {b.address && <a href={`https://www.openstreetmap.org/search?query=${q}`} target="_blank" rel="noreferrer" className="flex items-start gap-2 text-sm text-zinc-600 hover:underline"><MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brand }} /> {b.address}</a>}
              {b.hours && <div className="flex items-start gap-2 text-sm text-zinc-600"><Clock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: brand }} /> {b.hours}</div>}
              {b.phone && <a href={`tel:${b.phone}`} className="flex items-center gap-2 text-sm text-zinc-600"><Phone className="h-4 w-4 shrink-0" style={{ color: brand }} /> {b.phone}</a>}
            </div>
          </article>
        );
      })}
    </div>
  );
}

export function CardRewards({ rewards, brand, t = (k) => k }) {
  return (
    <div className="grid gap-8 lg:grid-cols-[360px_1fr] items-start">
      <div className="space-y-3" data-testid="cl-card-preview">
        <div className="overflow-hidden rounded-3xl"><LoyaltyCard customer={{ name: "Mario Rossi", card_number: "0000000000000", points: 1240 }} size="normal" /></div>
        <p className="text-sm text-zinc-600">{t("cardDesc")}</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="cl-rewards">
        {rewards.map((r) => (
          <div key={r.id} className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden" data-testid={`cl-reward-${r.id}`}>
            {img(r.image_url || r.image) ? <img src={img(r.image_url || r.image)} alt="" className="h-24 w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="h-24 flex items-center justify-center" style={{ background: `${brand}14` }}><Gift className="h-7 w-7" style={{ color: brand }} /></div>}
            <div className="p-3"><div className="text-sm font-semibold text-zinc-900 line-clamp-2">{r.name}</div>{r.points_cost != null && <div className="text-xs font-mono font-bold mt-1" style={{ color: brand }}>{r.points_cost} {t("pts")}</div>}</div>
          </div>
        ))}
        {rewards.length === 0 && <p className="text-sm text-zinc-500 col-span-full">{t("noRewards")}</p>}
      </div>
    </div>
  );
}

export function FlyersOffers({ flyers, offers, brand, promos = [], initiatives = [], t = (k) => k }) {
  return (
    <div className="space-y-6">
      {initiatives.length > 0 && (
        <div data-testid="cl-initiatives">
          <div className="text-[11px] uppercase tracking-widest text-zinc-700 font-bold mb-2">{t("initiatives")}</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {initiatives.map((d) => <Link key={d.id} to={`/scopri/iniziativa/${d.slug}`} data-testid={`cl-initiative-${d.slug}`}><InitiativeTile d={d} /></Link>)}
          </div>
        </div>
      )}
      {promos.length > 0 && (
        <div data-testid="cl-promo-products">
          <div className="text-[11px] uppercase tracking-widest text-rose-700 font-bold mb-2">{t("promoProducts")}</div>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {promos.map((p) => (
              <a key={p.id} href="#shop" className="shrink-0 w-40 rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden" data-testid={`cl-promo-product-${p.id}`}>
                {img(p.image_url) ? <img src={img(p.image_url)} alt="" className="h-24 w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="h-24 flex items-center justify-center" style={{ background: `${brand}14` }}><Tag className="h-6 w-6" style={{ color: brand }} /></div>}
                <div className="p-2.5"><div className="text-xs font-semibold text-zinc-900 line-clamp-1">{p.name}</div><div className="text-sm font-bold" style={{ color: "#E11D48" }}><s className="text-[11px] font-normal text-zinc-400 mr-1">€ {Number(p.price).toFixed(2)}</s>€ {Number(p.promo_price).toFixed(2)}</div>{p.promo_until && <div className="text-[10px] text-zinc-500">{t("validUntil")} {p.promo_until}</div>}</div>
              </a>
            ))}
          </div>
        </div>
      )}
    <div className="grid gap-6 md:grid-cols-2">
      <div className="space-y-3" data-testid="cl-flyers">
        {flyers.map((f) => (
          <a key={f.id} href={img(f.pdf_url || f.file_url) || f.link_url || "#"} target="_blank" rel="noreferrer" className="flex gap-4 rounded-2xl bg-white ring-1 ring-zinc-200 p-3 hover:shadow-md transition-shadow" data-testid={`cl-flyer-${f.id}`}>
            {img(f.cover_url || f.image_url) ? <img src={img(f.cover_url || f.image_url)} alt="" className="h-24 w-20 object-cover rounded-xl" /> : <div className="h-24 w-20 rounded-xl flex items-center justify-center" style={{ background: `${brand}14` }}><FileText className="h-7 w-7" style={{ color: brand }} /></div>}
            <div className="min-w-0"><div className="font-semibold text-zinc-900">{f.title}</div>{f.valid_until && <div className="text-xs text-zinc-500 mt-1">{t("validUntil")} {f.valid_until.slice(0, 10)}</div>}<div className="text-sm font-semibold mt-2 inline-flex items-center gap-1" style={{ color: brand }}>{t("browse")} <ArrowRight className="h-4 w-4" /></div></div>
          </a>
        ))}
        {flyers.length === 0 && <p className="text-sm text-zinc-500">{t("noFlyers")}</p>}
      </div>
      <div className="space-y-3" data-testid="cl-offers">
        {offers.map((o) => (
          <Link key={o.id} to={`/home/news/${o.id}`} className="flex gap-3 rounded-2xl bg-white ring-1 ring-zinc-200 p-3" data-testid={`cl-offer-${o.id}`}>
            <div className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${brand}14` }}><Tag className="h-5 w-5" style={{ color: brand }} /></div>
            <div className="min-w-0"><div className="font-semibold text-zinc-900 line-clamp-1">{o.title}</div><p className="text-sm text-zinc-600 line-clamp-2">{o.excerpt || o.body}</p></div>
          </Link>
        ))}
        {offers.length === 0 && <p className="text-sm text-zinc-500">{t("noOffers")}</p>}
      </div>
    </div>
    </div>
  );
}

export function GiftSection({ gift, brand, appName, onCta, t = (k) => k }) {
  const themes = gift.themes || [];
  return (
    <div className="grid gap-8 md:grid-cols-2 items-center">
      <GiftCardVisual theme={themes[0]} image_url={gift.image_url} amount={gift.denominations?.[1] || 25} code="GC-XXXX-XXXX" recipient_name="Giulia" sender_name="Marco" app_name={appName} />
      <div>
        <p className="text-base text-zinc-700">{gift.intro}</p>
        <div className="flex flex-wrap gap-2 mt-4">{themes.map((x) => <span key={x.id} className="text-[11px] font-semibold rounded-full px-3 py-1" style={{ background: `linear-gradient(135deg,${x.from},${x.to})`, color: "#fff" }}>{x.label}</span>)}</div>
        <Link to="/home/gift-card" onClick={onCta} className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 font-semibold" style={{ background: brand, color: "#fff" }} data-testid="cl-gift-cta"><Gift className="h-4 w-4" /> {t("giftCta")} {gift.title || "Gift Card"}</Link>
      </div>
    </div>
  );
}

export function NewsList({ news, brand }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="cl-news">
      {news.map((n) => (
        <Link key={n.id} to={`/home/news/${n.id}`} className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden hover:shadow-md transition-shadow" data-testid={`cl-news-${n.id}`}>
          {img(n.image_url) ? <img src={img(n.image_url)} alt="" className="h-36 w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="h-36 flex items-center justify-center" style={{ background: `${brand}14` }}><Newspaper className="h-8 w-8" style={{ color: brand }} /></div>}
          <div className="p-4"><div className="text-[11px] uppercase tracking-widest text-zinc-500">{n.created_at?.slice(0, 10)}</div><div className="font-semibold text-zinc-900 mt-1 line-clamp-2">{n.title}</div></div>
        </Link>
      ))}
    </div>
  );
}

export function Products({ products, brand }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="cl-products">
      {products.map((p) => (
        <div key={p.id} className="rounded-2xl bg-white ring-1 ring-zinc-200 overflow-hidden" data-testid={`cl-product-${p.id}`}>
          {img(p.image_url) ? <img src={img(p.image_url)} alt="" className="h-28 w-full object-cover" /> : <div className="h-28 flex items-center justify-center" style={{ background: `${brand}14` }}><ShoppingBag className="h-7 w-7" style={{ color: brand }} /></div>}
          <div className="p-3"><div className="text-sm font-semibold text-zinc-900 line-clamp-1">{p.name}</div>{p.price != null && <div className="text-xs font-bold mt-1" style={{ color: brand }}>€ {Number(p.price).toFixed(2)}</div>}</div>
        </div>
      ))}
    </div>
  );
}

export function Steps({ steps, brand }) {
  return (
    <ol className="grid gap-4 md:grid-cols-3" data-testid="cl-steps">
      {steps.map((s, i) => (
        <li key={i} className="rounded-2xl bg-white/10 ring-1 ring-white/20 p-5"><span className="font-heading text-4xl font-extrabold" style={{ color: "rgba(255,255,255,.45)" }}>0{i + 1}</span><p className="mt-2 font-semibold" style={{ color: "#fff" }}>{s}</p></li>
      ))}
    </ol>
  );
}

export function Testimonials({ items, brand, reviews = [], appName = "", t: tr = (k) => k }) {
  const all = [...reviews.map((r) => ({ ...r, isReview: true })), ...items];
  return (
    <div className="grid gap-4 md:grid-cols-3" data-testid="cl-testimonials">
      {all.map((t, i) => (
        <figure key={t.id || i} className="rounded-2xl bg-white ring-1 ring-zinc-200 p-5" data-testid={t.isReview ? `cl-review-${t.id}` : undefined}>
          {t.isReview ? <div className="flex gap-0.5" aria-label={`${t.stars} stelle`}>{[1, 2, 3, 4, 5].map((n) => <Star key={n} className="h-4 w-4" style={{ color: n <= t.stars ? "#F59E0B" : "#E4E4E7", fill: n <= t.stars ? "#F59E0B" : "none" }} />)}</div> : <Quote className="h-5 w-5" style={{ color: brand }} />}
          <blockquote className="mt-3 text-sm text-zinc-700 leading-relaxed">{t.text}</blockquote>
          <figcaption className="mt-3 text-xs font-semibold text-zinc-900">{t.name}{t.role && <span className="text-zinc-500 font-normal"> · {t.role}</span>}{t.isReview && t.created_at && <span className="text-zinc-400 font-normal"> · {t.created_at.slice(0, 10)}</span>}</figcaption>
          {t.isReview && t.reply && (
            <div className="mt-3 rounded-xl bg-zinc-50 border-l-2 pl-3 pr-3 py-2 text-xs" style={{ borderColor: brand }} data-testid={`cl-review-reply-${t.id}`}>
              <div className="font-semibold" style={{ color: brand }}>{tr("replyFrom")} {appName}{t.replied_at && <span className="text-zinc-400 font-normal"> · {t.replied_at.slice(0, 10)}</span>}</div>
              <p className="mt-1 text-zinc-700 leading-relaxed">{t.reply}</p>
            </div>
          )}
        </figure>
      ))}
    </div>
  );
}

export function Contacts({ contacts, social, brand, t = (k) => k }) {
  const rows = [
    contacts?.phone && { icon: Phone, label: contacts.phone, href: `tel:${contacts.phone}` },
    contacts?.whatsapp && { icon: MessageCircle, label: "WhatsApp", href: `https://wa.me/${contacts.whatsapp.replace(/\D/g, "")}` },
    contacts?.email && { icon: Mail, label: contacts.email, href: `mailto:${contacts.email}` },
    social?.website && { icon: Globe, label: social.website.replace(/^https?:\/\//, ""), href: social.website },
    social?.instagram && { icon: Instagram, label: "Instagram", href: social.instagram },
    social?.facebook && { icon: Facebook, label: "Facebook", href: social.facebook },
    social?.tiktok && { icon: Globe, label: "TikTok", href: social.tiktok },
  ].filter(Boolean);
  return (
    <div data-testid="cl-contacts">
      {contacts?.intro && <p className="text-base text-zinc-700 mb-4">{contacts.intro}</p>}
      <div className="flex flex-wrap gap-2">{rows.map((r, i) => <a key={i} href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white ring-1 ring-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-800 hover:shadow" data-testid={`cl-contact-${i}`}><r.icon className="h-4 w-4" style={{ color: brand }} /> {r.label}</a>)}</div>
      {rows.length === 0 && <p className="text-sm text-zinc-500">{t("contactsNA")}</p>}
    </div>
  );
}
